import { Track } from "../../../interfaces/dtos/track"
import { trackRoutes } from "../TrackNavigation";
import { TrackListItem } from "./TrackListItem";
import { ListAndDetail, useAlerts, useFeedback } from "@sis/react-library";
import { canAlterTrack, statusKeys, statusItems } from "../trackPage/trackPageHelper";
import { useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { trackState } from "../../../redux/track/trackSlice";
import { deleteTracks, setActive, setInactive, uploadTracksFile } from "../../../redux/track/trackAction";
import { TrackListDeleteConfirmation } from "./TrackListDeleteConfirmation";
import { TrackListChangeStatusConfirmation } from "./TrackListChangeStatusConfirmation";
import { TrackListChangeStatusConfirmationTitle } from "./TrackListChangeStatusConfirmationTitle";
import { dispatchIsSuccessful } from "../../../utils/commonUtils";
import { ChangeEvent, useRef } from "react";
import { trackActionKeys, trackAdditionalActions } from "./TrackListAdditionalActions";
import { GetCountryOptions, GetStatusOptions } from "../../shared/ListOptions";
import { CommonCanUpdateDesc } from "../../../utils/commonRules";

interface TrackListProps {
    provider: string;
    tracks: Track[]
}

export const TrackList = ({
    provider, tracks
}: TrackListProps) => {

    const state = useAppSelector(trackState);
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const inputFile = useRef(null);

    const { confirm } = useAlerts();
    const { showSuccess } = useFeedback();

    const statusOptions = GetStatusOptions();
    const countryOptions = GetCountryOptions(tracks);

    const handleAdditionalActionClick = async (
        key: string, tracksSelected: Track[], clearCheckedItems: () => void
    ) => {
        switch (key) {
            case trackActionKeys.upload:
                uploadFile();
                break;
            case trackActionKeys.setActive:
                setTracksStatus(tracksSelected, statusKeys.active, clearCheckedItems);
                break;
            case trackActionKeys.setInactive:
                setTracksStatus(tracksSelected, statusKeys.inactive, clearCheckedItems);
                break;
        }
    }

    const uploadFile = () => {
        // allow parameter to be any
        // eslint-disable-next-line
        if (inputFile) { (inputFile.current as any)?.click(); }
    }

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (e && e.target && e.target.files && e.target.files.length > 0) {
            const result = await dispatch(uploadTracksFile({ provider, file: e.target.files[0] }))
            e.target.value = '';
            if (dispatchIsSuccessful(result)) {
                showSuccess("File was uploaded successfully.");
            }
        }
    }

    const setTracksStatus = (
        tracksToUpdate: Track[], statusCode: string, clearCheckedItems: () => void
    ) => {
        const confirmTitle = <TrackListChangeStatusConfirmationTitle statusCode={statusCode} />;
        const confirmText = <TrackListChangeStatusConfirmation tracks={tracksToUpdate} statusCode={statusCode} />;

        confirm(confirmTitle, confirmText,
            () => dispatchTracksStatus(tracksToUpdate, statusCode, clearCheckedItems),
            "Yes", "No"
        )
    }

    const dispatchTracksStatus = async (
        tracksToUpdate: Track[], statusCode: string, clearCheckedItems: () => void
    ) => {
        const result = await dispatch(
            statusCode === statusKeys.active ?
                setActive(tracksToUpdate) :
                setInactive(tracksToUpdate)
        );
        if (dispatchIsSuccessful(result)) {
            showSuccess("Statuses were updated.");
            clearCheckedItems();
        }
    }

    const handleDelete = async (tracksToDelete: Track[]) => {
        const result = await dispatch(deleteTracks(tracksToDelete));
        if (dispatchIsSuccessful(result)) {
            showSuccess("Tracks were deleted.");
        }
    }

    const headercolor1 = "rgb(80, 80, 80)";
    const headercolor2 = "rgb(114, 114, 114)";

    const listItemRender = (track: Track, hasSelection: boolean) => <TrackListItem track={track} hasSelection={hasSelection} />;
    const listDeleteConfirmCustomText = (tracksSelected: Track[]) => <TrackListDeleteConfirmation tracks={tracksSelected} />;

    return (
        <>
            <ListAndDetail
                items={tracks}
                keyValue={(track) => track.key}
                itemRender={listItemRender}
                searchFields={['sportCode', 'shortDescription', 'longDescription', 'countryCode']}
                listUrl={trackRoutes.main}
                editUrl={trackRoutes.edit}
                newUrl={trackRoutes.new}
                deleteOptions={{
                    onDelete: handleDelete,
                    busyDeleting: state.deleting,
                    onDeleteConfirmCustomText: listDeleteConfirmCustomText
                }}
                additionalActions={{
                    items: trackAdditionalActions(state, theme),
                    onAdditionalActionClick: handleAdditionalActionClick
                }}
                checkOptions={{
                    checkDisabled: (track) => !canAlterTrack(track),
                    checkTooltip: (track) => canAlterTrack(track) ? undefined :
                        `You can't change the status or delete tracks for ${CommonCanUpdateDesc}`
                }}
                colours={{
                    headerBackground: `linear-gradient(to bottom right, ${headercolor1}, ${headercolor2},${headercolor2}, ${headercolor1})`,
                    headerColour: theme.palette.common.white,
                    borderColour: theme.palette.grey[700],
                    iconColour: theme.palette.common.white
                }}
                filters={
                    [{
                        description: "Country", filterItems: countryOptions,
                        filter: (item, filterValue) => item.countryCode === filterValue
                    },
                    {
                        description: "Status", filterItems: statusOptions,
                        filter: (item, filterValue) => item.isTrackActive === (filterValue === statusItems[0].key)
                    }]}
                listWidths={{ forInitialState: '500px', forURLSelected: '400px' }}
            />
            <input
                value={undefined} type='file' id='file' ref={inputFile} style={{ display: 'none' }}
                onChange={onFileChange}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
        </>
    )
}
